import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import {
  DashboardRounded,
  ShareRounded,
  ManageAccountsRounded,
  PeopleAltRounded,
  MailRounded,
  WarningRounded,
  QuestionAnswerRounded,
  DocumentScannerRounded,
  DeleteForeverRounded,
  PersonRemoveRounded,
  NotificationsActiveRounded,
  FavoriteRounded,
  VideoLibraryRounded,
  MenuBookRounded,
  AutoStoriesRounded,
} from "@mui/icons-material";
import logo from "../../../pictures/home/logo-m.png";

export default function Sidebar({
  drawerWidth = 260,
  mobileOpen,
  onDrawerToggle,
  activeComponent,
  handleComponentChange,
  currentUser,
}) {
  const adminListItems = [
    { name: "Dashboard", component: "trends", icon: <DashboardRounded /> },
    {
      name: "Verify Accounts",
      component: "verify-accounts",
      icon: <ManageAccountsRounded />,
    },
    { name: "All Users", component: "all-users", icon: <PeopleAltRounded /> },
    { name: "Posts", component: "posts", icon: <ShareRounded /> },
    { name: "System Messages", component: "system-messages", icon: <NotificationsActiveRounded /> },
    { name: "Support", component: "support", icon: <QuestionAnswerRounded /> },
    { name: "Chat", component: "chats", icon: <MailRounded /> },
    { name: "Reports", component: "reports", icon: <WarningRounded /> },
    { name: "Deletion Requests", component: "deletion-requests", icon: <PersonRemoveRounded /> },
    { name: "FAQs", component: "faqs", icon: <QuestionAnswerRounded /> },
    { name: "Documents", component: "legal", icon: <DocumentScannerRounded /> },
    { name: "Testimonials", component: "testimonials", icon: <FavoriteRounded /> },
    { name: "Videos", component: "videos", icon: <VideoLibraryRounded /> },
    { name: "Guidance", component: "guidance", icon: <MenuBookRounded /> },
    { name: "Daily Hadith", component: "hadith", icon: <AutoStoriesRounded /> },
  ];

  const userListItems = [
    {
      name: "Account Management",
      component: "disable-account",
      icon: <DashboardRounded />,
    },
  ];

  const list = currentUser?.role === "Manager" ? adminListItems : userListItems;

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#252018",
      }}
    >
      {/* Logo / Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.25,
          borderBottom: "1px solid rgba(234, 215, 167, 0.12)",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="MJMarry"
          sx={{ width: 56, filter: "brightness(1.4) saturate(.9)" }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 20,
              lineHeight: 1.1,
              color: "#f5efe3",
            }}
          >
            MJMarry
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              color: "#ead7a7",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".16em",
              textTransform: "uppercase",
            }}
          >
            {currentUser?.role === "Manager" ? "Admin" : "Account"}
          </Typography>
        </Box>
      </Box>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, mt: 1 }}>
        {list.map((item) => {
          const isActive = activeComponent === item.component;
          return (
            <ListItem key={item.name} disablePadding>
              <ListItemButton
                onClick={() => handleComponentChange(item.component)}
                selected={isActive}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  mx: 1.5,
                  my: 0.25,
                  py: 0.9,
                  px: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(199, 164, 93, 0.12)",
                    color: "#c7a45d",
                  },
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "rgba(199, 164, 93, 0.16)",
                    color: "#ead7a7",
                    "& .MuiListItemIcon-root": { color: "#c7a45d" },
                  },
                  "&.Mui-selected::before": {
                    content: '""',
                    position: "absolute",
                    left: -12,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 3,
                    backgroundColor: "#c7a45d",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#c7a45d" : "rgba(245, 239, 227, 0.55)",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer Section - pinned bottom */}
      <Box
        sx={{
          borderTop: "1px solid rgba(234, 215, 167, 0.12)",
          py: 2,
          px: 2,
          m: 0,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", p:0 , m: 0 }}>
          © {new Date().getFullYear()} MJMarry
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "#252018",
            color: "white",
            justifyContent: "space-between",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "#252018",
            color: "white",
            borderRight: "1px solid rgba(234, 215, 167, 0.12)",
            justifyContent: "space-between",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
